// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { createRouter, createWebHistory } from 'vue-router';
import { nextTick } from 'vue';
import store from '@/store';
import i18n from '@/i18n';
import analytics from '@/utils/analytics';
import sidebarComp from '@/components/sidebar.vue';
import authView from '@/views/auth/auth.vue';
import signinView from '@/views/auth/sign-in.vue';
import signupView from '@/views/auth/sign-up.vue';
import signoutView from '@/views/auth/sign-out.vue';
import claimView from '@/views/auth/claim.vue';
import signinMfaView from '@/views/auth/sign-in-mfa.vue';
import signinErrorView from '@/views/auth/sign-in-error.vue';
import unauthorizedView from '@/views/auth/unauthorized.vue';
import idpErrorView from '@/views/auth/idp-error.vue';
import { populateDataLayer } from '@/utils/dataLayerUtils';
import { mutationTypes as infoMutations } from '@/store/info';
import { actionTypes as userActions } from '@/store/user';
import { isAbortError } from '@/utils/apiUtils';

// Scopes guard requests to a single navigation so that navigating again before
// the page has finished loading cancels whatever the previous route started.
let navigationAbortController = new AbortController();

// Hidden, local-only routes. import.meta.env.DEV is false in production builds,
// so these are tree-shaken out of the wwwroot bundle and never shipped.
const devRoutes = import.meta.env.DEV
    ? [
          {
              path: '/demo/mfa-setup',
              name: 'MFA Setup Demo',
              meta: { title: 'MFA Setup Demo' },
              components: {
                  sidebar: sidebarComp,
                  default: authView,
              },
              children: [
                 {
                      path: '',
                      name: 'MFA Setup Demo',
                      meta: { title: 'MFA Setup Demo' },
                      component: () => import('@/views/demo/mfa-setup-demo.vue'),
                  }
              ]
          },
      ]
    : [];

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            components: {
                sidebar: sidebarComp,
                default: authView,
            },
            children: [
                {
                    path: '',
                    alias: '/signin',
                    name: 'Sign In',
                    meta: { title: 'Sign In' },
                    component: signinView,
                },
                {
                    path: 'signin/mfa',
                    name: 'Sign In MFA',
                    meta: { title: 'Sign In' },
                    component: signinMfaView,
                },
                {
                    path: 'signup',
                    name: 'Sign Up',
                    meta: { title: 'Sign Up' },
                    component: signupView,
                },
                {
                    path: 'reset',
                    name: 'Reset Password',
                    meta: { title: 'Sign In' },
                    component: signinView,
                    props: { initialResetPasswordModal: true },
                },
                {
                    path: 'confirmprofile',
                    name: 'Confirm Profile',
                    meta: { title: 'Sign In' },
                    component: signinView,
                    props: { confirmProfile: true },
                },
                {
                    path: 'timeout',
                    name: 'Timeout',
                    meta: { title: 'Sign In' },
                    component: signinView,
                    props: { timeout: true },
                },
                {
                    path: 'expiredreset',
                    name: 'Expired Password Reset Token',
                    meta: { title: 'Sign In' },
                    component: signinView,
                    props: { resetPasswordExpired: true },
                },
            ],
        },
        {
            path: '/claimaccount',
            name: 'Claim Account',
            meta: { title: 'Claim Account' },
            components: {
                sidebar: sidebarComp,
                default: claimView,
            },
            props: {
                sidebar: { auth: true },
                default: (route) => ({
                    destination: route.query.returnUrl,
                    userId: route.query.userId,
                    token: route.query.token,
                    allowGuest: route.query.allowGuest !== 'false',
                }),
            },
            beforeEnter: (to, from, next) => {
                store
                    .dispatch(userActions.IS_ACCOUNT_CLAIMED, {
                        userId: to.query.userId,
                        token: to.query.token,
                        url: to.query.returnUrl,
                        signal: navigationAbortController.signal,
                    })
                    .then((response) => {
                        if (response.status === 200) {
                            next();
                        } else {
                            throw Error(response.statusText);
                        }
                    })
                    .catch((error) => {
                        if (isAbortError(error)) {
                            return;
                        }

                        store.commit(infoMutations.ADD_TOAST, {
                            color: 'red',
                            icon: 'warning_small',
                            timer: 6000,
                            text: i18n.global.t('authRouterText.error'),
                        });
                        next('/');
                    });
            },
        },
        {
            path: '/verify',
            name: 'Email Verification',
            meta: { title: 'Sign In' },
            components: {
                sidebar: sidebarComp,
                default: signinView,
            },
            beforeEnter: (to, from, next) => {
                store
                    .dispatch(userActions.VERIFY_EMAIL, {
                        userId: to.query.userId,
                        token: to.query.token,
                        returnUrl: to.query.returnUrl,
                        signal: navigationAbortController.signal,
                    })
                    .then((r) =>
                        r
                            .text()
                            .then((data: any) => ({ status: r.status, body: data }))
                    )
                    .then((response) => {
                        if (response.status === 200) {
                            (window as Window).location =
                                response.body;
                        }
                    })
                    .catch((error) => {
                        if (isAbortError(error)) {
                            return;
                        }

                        store.commit(infoMutations.ADD_TOAST, {
                            color: 'red',
                            icon: 'warning_small',
                            timer: 6000,
                            text: i18n.global.t('authRouterText.error'),
                        });
                        next();
                    });
            },
        },
        {
            path: '/signout',
            name: 'Sign Out',
            meta: { title: 'Sign Out' },
            props: {
                default: (route) => ({ id: route.query.id }),
            },
            components: {
                sidebar: sidebarComp,
                default: signoutView,
            },
        },
        {
            path: '/unauthorized',
            name: 'Unauthorized',
            meta: { title: 'Unauthorized' },
            props: {
                default: (route) => ({ app: route.query.app }),
            },
            components: {
                sidebar: sidebarComp,
                default: unauthorizedView,
            },
        },
        {
            path: '/idp-error',
            name: 'Identity Provider Error',
            meta: { title: 'Identity Provider Error' },
            components: {
                sidebar: sidebarComp,
                default: idpErrorView,
            },
        },
        {
            path: '/signin/error',
            name: 'Sign In Error',
            meta: { title: 'Sign In Error' },
            components: {
                sidebar: sidebarComp,
                default: signinErrorView,
            },
        },
        ...devRoutes,
        {
            path: '/*',
            redirect: '/',
        },
    ],
});

router.beforeEach((to, from, next) => {
    navigationAbortController.abort();
    navigationAbortController = new AbortController();

    document.title = `Protolabs · ${to.meta.title}`;

    const region = to.query.rgn || store.state.geolocation?.defaultRegionCode;
    // @ts-ignore
    populateDataLayer(region);

    store.commit(infoMutations.REMOVE_MODAL);
    store.commit(infoMutations.REMOVE_TOAST);
    store.commit(infoMutations.REMOVE_BANNER);

    next();
});

router.afterEach((to, from) => {
    nextTick(() => {
        analytics.identify();
        // @ts-ignore
        analytics.page(to.name);
    });
});

export default router;
